import type { jsPDF } from 'jspdf'
import type { TableCol, TableCell } from './report-types'

// ── PDF Layout Constants ──────────────────────────────────────────────
export const PAGE_W = 210
export const PAGE_H = 297
export const MARGIN_L = 15
export const MARGIN_R = 15
export const MARGIN_T = 25
export const MARGIN_B = 18
export const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R
export const LH = 5.5
export const SEC_GAP = 6
export const QR_SIZE = 28

// ── Design System Colors ──────────────────────────────────────────────
export const ACCENT = { r: 37, g: 99, b: 235 }       // Blue-600
export const ACCENT_LIGHT = { r: 219, g: 234, b: 254 } // Blue-100
export const ACCENT_BG = { r: 239, g: 246, b: 255 }   // Blue-50
export const GRAY_50 = { r: 249, g: 250, b: 251 }
export const GRAY_100 = { r: 243, g: 244, b: 246 }
export const GRAY_200 = { r: 229, g: 231, b: 235 }
export const GRAY_400 = { r: 156, g: 163, b: 175 }
export const GRAY_500 = { r: 107, g: 114, b: 128 }
export const GRAY_700 = { r: 55, g: 65, b: 81 }
export const GRAY_900 = { r: 17, g: 24, b: 39 }
export const GREEN = { r: 22, g: 163, b: 74 }
export const ORANGE = { r: 234, g: 88, b: 12 }
export const RED = { r: 220, g: 38, b: 38 }

// ── SVG Landscape page dimensions (matching useExportSvg) ─────────────
export const SVG_W = 860
export const SVG_H = 600
export const PDF_LANDSCAPE_W = 297
export const PDF_LANDSCAPE_H = 210

// ── Number formatting (jsPDF cannot render U+202F from toLocaleString) ─
export function fmtNum(n: number): string {
  if (n === 0) return '0'
  const s = Math.abs(Math.round(n)).toString()
  const parts: string[] = []
  for (let i = s.length; i > 0; i -= 3) {
    parts.unshift(s.slice(Math.max(0, i - 3), i))
  }
  return (n < 0 ? '-' : '') + parts.join(' ')
}

export function fmtDec(n: number, decimals = 1): string {
  return n.toFixed(decimals).replace('.', ',')
}

// ── PDF Drawing Helpers ─────────────────────────────────────────────

export function pageBreak(doc: jsPDF, y: number, need: number): number {
  if (y + need > PAGE_H - MARGIN_B) { doc.addPage(); return MARGIN_T }
  return y
}

export function newTechnicalPage(doc: jsPDF): void {
  doc.addPage('a4', 'portrait')
}

export function drawPageHeader(
  doc: jsPDF,
  sectionName: string,
  projectName: string,
): number {
  // Accent bar top
  doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b)
  doc.rect(0, 0, PAGE_W, 3, 'F')

  // Section name
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
  doc.text(sectionName, MARGIN_L, 16)

  // Project name right-aligned
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(GRAY_400.r, GRAY_400.g, GRAY_400.b)
  doc.text(projectName, PAGE_W - MARGIN_R, 16, { align: 'right' })

  // Thin line under header
  doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
  doc.setLineWidth(0.3)
  doc.line(MARGIN_L, 20, PAGE_W - MARGIN_R, 20)

  return MARGIN_T
}

export function sectionTitle(doc: jsPDF, title: string, y: number): number {
  y = pageBreak(doc, y, 14)
  // Accent left bar
  doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b)
  doc.rect(MARGIN_L, y - 1, 2.5, 7, 'F')
  // Title text
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
  doc.text(title.toUpperCase(), MARGIN_L + 5, y + 4)
  return y + 12
}

export function subSectionTitle(doc: jsPDF, title: string, y: number): number {
  y = pageBreak(doc, y, 10)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8.5)
  doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
  doc.text(title, MARGIN_L, y + 3)
  doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
  doc.setLineWidth(0.15)
  doc.line(MARGIN_L, y + 5, PAGE_W - MARGIN_R, y + 5)
  return y + 9
}

export function tableHeader(doc: jsPDF, cols: TableCol[], y: number): number {
  y = pageBreak(doc, y, 10)
  // Header background
  doc.setFillColor(GRAY_100.r, GRAY_100.g, GRAY_100.b)
  doc.rect(MARGIN_L, y - 3, CONTENT_W, 7, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
  for (const c of cols) doc.text(c.label.toUpperCase(), c.x, y + 1, { align: c.align || 'left' })
  return y + 7
}

let rowIndex = 0
export function resetRowIndex(): void { rowIndex = 0 }

export function tableRow(doc: jsPDF, cols: TableCell[], y: number): number {
  y = pageBreak(doc, y, LH + 1)
  // Alternating row background
  if (rowIndex % 2 === 0) {
    doc.setFillColor(GRAY_50.r, GRAY_50.g, GRAY_50.b)
    doc.rect(MARGIN_L, y - 3.5, CONTENT_W, LH + 1, 'F')
  }
  rowIndex++
  doc.setFontSize(7.5)
  doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
  for (const c of cols) {
    doc.setFont('helvetica', c.bold ? 'bold' : 'normal')
    if (c.bold) doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
    else doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
    doc.text(c.text, c.x, y, { align: c.align || 'left' })
  }
  return y + LH
}

export function totalRow(doc: jsPDF, y: number, cols: TableCell[]): number {
  // Top line
  doc.setDrawColor(GRAY_400.r, GRAY_400.g, GRAY_400.b)
  doc.setLineWidth(0.3)
  doc.line(MARGIN_L, y - 1, PAGE_W - MARGIN_R, y - 1)
  // Background
  doc.setFillColor(ACCENT_BG.r, ACCENT_BG.g, ACCENT_BG.b)
  doc.rect(MARGIN_L, y, CONTENT_W, LH + 1, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  doc.setTextColor(ACCENT.r, ACCENT.g, ACCENT.b)
  for (const c of cols) doc.text(c.text, c.x, y + 3, { align: c.align || 'left' })
  return y + LH + SEC_GAP
}

export function kv(doc: jsPDF, label: string, value: string, y: number, x: number): number {
  y = pageBreak(doc, y, LH + 1)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
  doc.text(label, x, y)
  doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
  doc.setFont('helvetica', 'bold')
  doc.text(value, x + doc.getTextWidth(label) + 2, y)
  return y + LH
}

export function sep(doc: jsPDF, y: number): number {
  doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
  doc.setLineWidth(0.15)
  doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y)
  return y + 3
}

// ── Metric Box (for dashboard cards) ─────────────────────────────────
export function drawMetricBox(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  label: string, value: string,
  accent = false,
): void {
  if (accent) {
    doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b)
    doc.roundedRect(x, y, w, h, 2, 2, 'F')
    doc.setTextColor(255, 255, 255)
  } else {
    doc.setFillColor(255, 255, 255)
    doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
    doc.setLineWidth(0.3)
    doc.roundedRect(x, y, w, h, 2, 2, 'FD')
    doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
  }
  // Value (large)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text(value, x + w / 2, y + h / 2 - 1, { align: 'center' })
  // Label (small below)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.5)
  if (accent) doc.setTextColor(219, 234, 254)
  else doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
  doc.text(label, x + w / 2, y + h / 2 + 7, { align: 'center' })
}

// ── Info Card (for LED wall detail, processor detail...) ─────────────
export function drawCard(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  title: string,
  items: [string, string][],
): number {
  y = pageBreak(doc, y, h + 2)
  // Card border
  doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
  doc.setLineWidth(0.3)
  doc.setFillColor(255, 255, 255)
  doc.roundedRect(x, y, w, h, 2, 2, 'FD')
  // Title bar
  doc.setFillColor(GRAY_100.r, GRAY_100.g, GRAY_100.b)
  doc.roundedRect(x, y, w, 8, 2, 2, 'F')
  doc.rect(x, y + 4, w, 4, 'F') // fill bottom corners
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
  doc.text(title, x + 4, y + 5.5)
  // Content rows
  let iy = y + 12
  for (const [label, value] of items) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
    doc.text(label, x + 4, iy)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
    doc.text(value, x + w - 4, iy, { align: 'right' })
    iy += 5
  }
  return y + h + 3
}

// ── Badge (for status indicators) ────────────────────────────────────
export function drawBadge(
  doc: jsPDF,
  x: number, y: number,
  text: string,
  color: { r: number; g: number; b: number },
): void {
  const tw = doc.getTextWidth(text) + 4
  doc.setFillColor(color.r, color.g, color.b)
  doc.roundedRect(x, y - 3, tw, 5, 1.5, 1.5, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(5.5)
  doc.setTextColor(255, 255, 255)
  doc.text(text, x + 2, y)
}

// ── SVG to PNG helper (for synoptic page embeds) ─────────────────────
export function svgToDataUrl(svgElement: SVGSVGElement): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) { reject(new Error('Canvas not available')); return }
    const svgCopy = svgElement.cloneNode(true) as SVGSVGElement
    svgCopy.setAttribute('width', String(SVG_W))
    svgCopy.setAttribute('height', String(SVG_H))
    const img = new Image()
    const blob = new Blob([new XMLSerializer().serializeToString(svgCopy)], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    img.onload = () => {
      canvas.width = SVG_W * 3; canvas.height = SVG_H * 3
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high'
      ctx.scale(3, 3); ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url); resolve(canvas.toDataURL('image/png', 1.0))
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('SVG render failed')) }
    img.src = url
  })
}

// ── Load image as base64 dataURL ─────────────────────────────────────
export function loadImageAsDataUrl(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) { reject(new Error('Canvas not available')); return }
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => reject(new Error('Image load failed'))
    img.src = src
  })
}
